import { useApolloClient } from '@apollo/client';
import { Loading } from '@perch-labs/shrike';
import { getQueryParams, removeProps } from '@perch-labs/warbler';
import { createContext, useEffect, useMemo, PropsWithChildren, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAsync } from '~/hooks';
import { CustomerTypes } from '~/screens/select-utility';
import { AddressType } from '~/typings/context';
import { AsyncReturnType } from '~/typings/utils';
import { removeKeys } from '~/utils/for-warbler';
import { queryParamsRedirectPath } from '~/utils/redirects';
import { store } from '~/utils/store';
import { trackPageView } from '~/utils/tracking';
import {
  handleAmeren,
  handleDocusign,
  handleGoogle,
  handlePromoCode,
  handleProjectIdFromQueryParams,
  handleResumingUser,
  handleUtilityIdAndZipCode,
  handleUtmParams,
} from './handlers';
import { handleCustomerType } from './handlers/handle-customer-type';
import { handleFunnelQueryParamsToken } from './handlers/handle-funnel-query-params-token';
import { handleSignnow } from './handlers/handle-signnow';
import { ParsedQueryParams, UnparsedQueryParams } from './types';

export const QueryParamsContext = createContext({} as QueryParamsContextType);

type Props = PropsWithChildren<{
  search?: string;
}>;

export type QueryParamsContextType = ParsedQueryParams & {
  setValidatedProjectId?: (projectId: number | undefined) => void;
};
/**
 *  React hook that asynchronously loads data dependent on the current QueryParams.
 *  once loading is false the contents of context are usable.
 */
export const useParamHandlers = (
  params: UnparsedQueryParams,
  searchString: string,
): {
  loading: boolean;
  context: ParsedQueryParams;
} => {
  const apolloClient = useApolloClient();

  const { loading, value: validatedParams = [] } = useAsync((): Promise<ValidatedParams> => {
    return (async () => {
      const [
        funnelQueryParams,
        googleUser,
        promoCode,
        resumingUser,
        zipAndUtility,
        validatedCustomerType,
      ] = await Promise.all([
        handleFunnelQueryParamsToken({ apolloClient, queryParams: params }),
        handleGoogle({ apolloClient, params, searchString }),
        handlePromoCode({ apolloClient, params }),
        handleResumingUser({ apolloClient, params }),
        handleUtilityIdAndZipCode({ apolloClient, params }),
        handleCustomerType(params),
      ]);

      // Match parsed context: token-supplied utilityId can skip the utility form.
      const resolvedUtilityId = funnelQueryParams?.params.utilityId || zipAndUtility?.utilityId;

      // Validate projectId after utility resolution so skipped utility steps still validate.
      const projectIdValidation = await handleProjectIdFromQueryParams({
        apolloClient,
        params,
        utilityId: resolvedUtilityId,
      });

      return [
        funnelQueryParams,
        googleUser,
        promoCode,
        resumingUser,
        zipAndUtility,
        validatedCustomerType,
        projectIdValidation,
      ] as ValidatedParams;
    })();
  });

  const {
    accountId,
    customPlanId,
    enrollmentOption,
    email,
    fName,
    firstName,
    inviteCode,
    lName,
    lastName,
    preferredBillingExperience,
    preferredEnrollment,
    phone,
    reason,
    terms,
    utilityAccountId,
    businessName,
    fico,
    businessPhone,
    jobTitle,
    perchSfProductId,
    programId,
    ira,
    isLmi,
    signupSource,
  } = params;

  const [
    funnelQueryParams,
    googleUser,
    promoCode,
    resumingUser,
    zipAndUtility,
    validatedCustomerType,
    projectIdValidation,
  ] = validatedParams;

  if (googleUser?.data) {
    /**
     * If we have a google user, we store it in the store.
     * This is needed when user reloads the page accidentally when in google login
     * page. This must be reset once the user is redirected away from google sign up.
     */
    store.set('GOOGLE_USER', googleUser);
  }
  const amerenUser = handleAmeren({ params });
  const dashboardReason = location.pathname === '/dashboard' ? reason : undefined;

  const redirectPath = validatedParams.length
    ? queryParamsRedirectPath({ amerenUser, dashboardReason, googleUser, resumingUser })
    : null;
  const {
    serviceAddressStreetOne,
    serviceAddressCity,
    serviceAddressState,
    zipCode,
    serviceAddressStreetTwo,
    email: externalParamsEmail,
    firstName: externalParamsFirstName,
    meterNumber: externalParamsMeterNumber,
    annualizedUsageEstimate: externalParamsAnnualizedUsageEstimate,
    secondaryAccountIdentifier: externalParamsSecondaryAccountIdentifier,
    inviteCode: externalParamsInviteCode,
    lastName: externalParamsLastName,
    phone: externalParamsPhone,
    partnerSource: externalParamsPartnerSource,
    preferredEnrollment: externalParamsPreferredEnrollment,
  } = funnelQueryParams?.params || {};
  const address: AddressType = {
    street: serviceAddressStreetOne,
    city: serviceAddressCity,
    state: serviceAddressState,
    zipCode,
  };
  const values: QueryParamsContextType = {
    address,
    addressSecondary: serviceAddressStreetTwo,
    accountId,
    amerenUser,
    billing: params.billing,
    customPlanId,
    dashboardReason,
    docusignUser: handleDocusign({ params }),
    signnowUser: handleSignnow({ params }),
    email: externalParamsEmail || email,
    enrollmentOption,
    firstName: externalParamsFirstName || firstName || fName,
    googleUser: googleUser?.data,
    meterNumber: externalParamsMeterNumber || params.meterNumber,
    annualizedUsageEstimate:
      externalParamsAnnualizedUsageEstimate || params.annualizedUsageEstimate,
    secondaryAccountId: externalParamsSecondaryAccountIdentifier || params.secondaryAccountId,
    googleUserError: googleUser?.error,
    inviteCode: externalParamsInviteCode || inviteCode,
    lastName: externalParamsLastName || lastName || lName,
    phone: externalParamsPhone || (phone && String(phone)),
    partnerSource: externalParamsPartnerSource,
    partnerId: params.partnerId,
    preferredBillingExperience,
    preferredEnrollment: externalParamsPreferredEnrollment || preferredEnrollment,
    projectId: projectIdValidation?.status === 'valid' ? projectIdValidation.projectId : undefined,
    projectIdInvalid: projectIdValidation?.status === 'invalid' ? true : undefined,
    promoCode,
    redirectPath,
    // @ts-expect-error there is some type mimatch between what is coming from graphql
    // and how we are using userDocusignEnvelopes. TODO fix this.
    resumingUser: resumingUser?.data,
    terms,
    unparsedQueryParams: removeKeys(params, ['loginToken', 'verificationCode', 'code', 'state']),
    utilityAccountId,
    utilityAccountNumber: funnelQueryParams?.params.accountNumber || params.utilityAccountNumber,
    utilityId: funnelQueryParams?.params.utilityId || zipAndUtility?.utilityId,
    utmParams: handleUtmParams({ params }),
    zipCode: funnelQueryParams?.params.zipCode || zipAndUtility?.zipCode,
    customerType: validatedCustomerType,
    businessName,
    fico,
    ira,
    jobTitle: jobTitle && String(jobTitle),
    businessPhone: businessPhone && String(businessPhone),
    perchSfProductId,
    programId,
    zohoReferenceNumber:
      params.zohoReferenceNumber != null ? Number(params.zohoReferenceNumber) : undefined,
    dealerCode: params.dealerCode != null ? String(params.dealerCode) : undefined,
    clickId: params.clickId != null ? String(params.clickId) : undefined,
    isLmi,
    signupSource,
  };

  const cleanValues = removeProps(values, [null, undefined]) as ParsedQueryParams;
  return {
    loading,
    context: cleanValues,
  };
};

/**
 * Temporary fix for SUB-459.
 * TODO: Find the real reason query params are prefixed with amp_ and fix it.
 */
export const sanitizeParamsForAMP = (params: Record<string, any>): Record<string, any> => {
  const sanitizedParams: UnparsedQueryParams = {};
  for (const [key, value] of Object.entries(params)) {
    if (key.startsWith('amp_')) {
      sanitizedParams[key.replace('amp_', '')] = value;
    } else {
      sanitizedParams[key] = value;
    }
  }
  return sanitizedParams;
};

const CenteredLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100vw;
`;

export const QueryParamsProvider = ({ children, search }: Props) => {
  const searchString = search || location.search;
  const navigate = useNavigate();
  const [validatedProjectId, setValidatedProjectId] = useState<number | undefined>(undefined);
  const unsanitizedParams = useMemo(
    () => getQueryParams(searchString),
    // adding searchString to deps causes infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  ) as UnparsedQueryParams;
  const params = sanitizeParamsForAMP(unsanitizedParams);
  const { loading, context } = useParamHandlers(params, searchString);

  useEffect(() => {
    trackPageView();
    navigate(window.location.pathname, { replace: true }); // clear qps from url
  }, [navigate]);

  const value = useMemo(
    () => ({
      ...context,
      projectId: validatedProjectId ?? context.projectId,
      setValidatedProjectId,
    }),
    [context, validatedProjectId],
  );

  return (
    <QueryParamsContext.Provider value={value}>
      {loading ? (
        <CenteredLoader>
          <Loading />
        </CenteredLoader>
      ) : (
        children
      )}
    </QueryParamsContext.Provider>
  );
};

export type AmerenUser = ReturnType<typeof handleAmeren>;
export type GoogleUser = AsyncReturnType<typeof handleGoogle>;
export type funnelQueryParams = AsyncReturnType<typeof handleFunnelQueryParamsToken>;
export type ResumingUser = AsyncReturnType<typeof handleResumingUser>;

export type ValidatedParams = [
  funnelQueryParams,
  GoogleUser,
  AsyncReturnType<typeof handlePromoCode>,
  ResumingUser,
  AsyncReturnType<typeof handleUtilityIdAndZipCode>,
  CustomerTypes | undefined,
  AsyncReturnType<typeof handleProjectIdFromQueryParams>,
];
